import { store } from "../store/store";
import { qlikApp } from "./qlikApp";
import type { HostConfig } from "@qlik/api/auth";
import type { OpenAppSessionProps } from "@qlik/api/qix";
import { getQlikResources } from "./getQlikResources";
import { setHyperCubeData } from "../store/slice/qlikObjectSlice";
import { showSnackbar } from "../store/slice/snackbarSlice";
import { CYBER_CONFIG } from "../constants/cyberConfig";

// Redux helpers
const dispatch = store.dispatch;

const qlikAppCache: Record<string, any> = {};

// Define types for Qlik objects
interface QlikServerDef {
    host: string;
    port: number;
    secure: boolean;
    preifx: string;
  }

interface QlikAppDef {
  serverRef: string;
  appId: OpenAppSessionProps;
}

interface QlikVisualizationDef {
  type: string;
  id: string;
  options?: Record<string, any>;
  ui: string;
  appRef: string;
  sheetId?: string;
}

interface GenericObjectDef {
  objectDef: Record<string, any>;
}


export async function getQlikApp(name: string | undefined): Promise<any> {
  if (!name) return null;

  // Check cache first
  if (qlikAppCache[name]) {
    return qlikAppCache[name];
  }

  const appConfig = await getQlikResources("qlik-apps");
  const appDef: QlikAppDef = appConfig[name];
  // const tenantDef: HostConfig | any = state.qlikResources.QlikServerDefs[appDef?.tenantRef];
  const serverConfig = await getQlikResources("qlik-servers");
  const serverDef: HostConfig | any = serverConfig[appDef?.serverRef];

  if (serverDef) {
        const { host, port, secure, prefix, authType } = serverDef;
        const qlikServerPrefix = prefix !== "" ? `/${prefix}` : "";
        const qlikServerPath = `${(secure ? "https://" : "http://") + host + (port ? `:${port}` : "") + qlikServerPrefix}/`;
        // const qlikServerPath = `${(secure ? "https://" : "http://") + host + qlikServerPrefix}/`;
        const { appId } = appDef;
        const config = { host: qlikServerPath, authType };

        //   qlikAppPromise = await qlikApp(config, appId);
        //   dispatch(setQdtAppPromise({ name, appPromise: qlikAppPromise }));
        const qlikApplication = await qlikApp(config, appId);
        // dispatch(setQlikApplication({ name, app: qlikApplication.id }));
        await qlikApplication.waitForOpen.promise;
        
        // Store in cache
        qlikAppCache[name] = qlikApplication;
        return qlikApplication;
  }
}

export async function readAppLastUpdateDate(appRef: string): Promise<any> {
  try {
    return new Promise(async (resolve) => {
      const qlikApp = await getQlikApp(appRef);
      const lastUpdateDate = await qlikApp.getAppLayout();
      resolve(lastUpdateDate.qLastReloadTime)
    })
  } catch (error) {
    console.error(`Error reading last update date from app: ${appRef}`, error);
    dispatch(showSnackbar({ reason: "error", message: CYBER_CONFIG.generalErrorMessage }));
  }
}

export async function getVisualizationProps(name: string): Promise<{ vizDef: QlikVisualizationDef | null; appId: string | null }> {
  // const state = getState();
  // const vizDef = state.qlikResources.qlikVisualizationDefs[name];
  const vizConfig = await getQlikResources("qlik-visualizations")
  const vizDef = vizConfig[name];
  // const appId = state.qlikResources.qlikAppDefs[vizDef?.appRef]?.appId;
  const appsConfig = await getQlikResources("qlik-apps");
  const appId = appsConfig[vizDef?.appRef]?.appId;
  //Return an object with vizDef and appId
  return { vizDef, appId };
}

async function readHyperCubeSessionObjData(appRef: string, hyperCubeCustomId: string, hyperCubeSessionObj: any): Promise<any> {
  const hyperCubeLayout = await hyperCubeSessionObj.getLayout();
  dispatch(setHyperCubeData({ name: appRef, hyperCubeCustomId, hyperCube: hyperCubeLayout.qHyperCube, hyperCubeId: hyperCubeLayout.qInfo.qId }));
}


export async function getHyperCubeData(
  appRef: string,
  hyperCubeCustomId: string,
  hyperCubeDef: any,
): Promise<any | null> {
  try {
    return new Promise(async (resolve) => {
      // Get the Qlik app instance
      const qlikApp = await getQlikApp(appRef);

      if (qlikApp) {
        // Create the generic object in the Qlik app
        const hyperCubeDefinition = {
          qInfo: {
            qType: "mashup"
          },
          qHyperCubeDef : hyperCubeDef,
        }
        const hyperCubeSessionObj = await qlikApp.createSessionObject(hyperCubeDefinition);
        // const sessionObjLayout = await sessionObj.getLayout();
        await readHyperCubeSessionObjData(appRef, hyperCubeCustomId, hyperCubeSessionObj);

        //Handling new selections applied
        hyperCubeSessionObj.on('changed', async () => {
          await readHyperCubeSessionObjData(appRef, hyperCubeCustomId, hyperCubeSessionObj);
        });

        // Store the generic object layout in the Redux store
        // dispatch(setGenericObject({ name: appRef, object: sessionObjLayout.objectDef }));

        resolve(true)
      }
    })
  } catch (error) {
    console.error(`Error creating generic object for appRef: ${appRef}`, error);
    dispatch(showSnackbar({ reason: "error", message: CYBER_CONFIG.generalErrorMessage }));
  }

  return null;
}

export async function getValueColorsFromMasterDim(appRef: string, dimId: string): Promise<any | null> {
  try {
    const app = await getQlikApp(appRef);
    if (!app) return;

    const colorMapModelObject = await app.getObject(`ColorMapModel_${dimId}`);
    const colorMapModelProps = await colorMapModelObject.getProperties();
    return colorMapModelProps.colorMap.colors || [];
  }
  catch (error) {
    console.error(`Error getting value colors from master dimension: ${dimId}`, error);
    dispatch(showSnackbar({ reason: "error", message: CYBER_CONFIG.generalErrorMessage }));
  }
}


export default {
  getQlikApp,
  readAppLastUpdateDate,
  getVisualizationProps,
  getHyperCubeData,
  getValueColorsFromMasterDim,
};